import { useState } from "react"
import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { Users, Sparkles, MessageSquare, Globe, ListChecks, MessageCircle, Wallet, TrendingUp, Heart, Hammer, Apple, Check } from "lucide-react"
import Layout from "@/components/Layout"
import AssistantCard from "@/components/AssistantCard"
import SectionHeader from "@/components/SectionHeader"
import { Button } from "@/components/ui/button"
import { ASSISTANTS_LIST, PRICING_PLANS, ROUTE_PATHS } from "@/lib/index"
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion"
import { IMAGES } from "@/assets/images"

export default function FamilyAccess() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly")

  const benefits = [
    { icon: Users, title: "5 Family Members", description: "Support for the whole household" },
    { icon: Sparkles, title: "22+ Assistants", description: "Practical help across all areas of life" },
    { icon: MessageSquare, title: "Monthly Message Credits", description: "Flexible usage for your family's needs" },
    { icon: Globe, title: "Local Language Support", description: "Communicate in your preferred language" },
    { icon: ListChecks, title: "Step-by-Step Guidance", description: "Clear, actionable advice you can follow" },
    { icon: MessageCircle, title: "Familiar Messaging Experience", description: "Works through WhatsApp - no app needed" },
  ]

  const savings = [
    { icon: Sparkles, title: "Save on Tutoring", description: "Homework help and exam prep", value: "R500+/month" },
    { icon: Wallet, title: "Budget Better", description: "Money coaching and planning", value: "R300+/month" },
    { icon: Heart, title: "Health Guidance", description: "Wellness and health advice", value: "R400+/month" },
    { icon: Hammer, title: "DIY Fixes", description: "Home repair guidance", value: "R200+/month" },
    { icon: Apple, title: "Meal Planning", description: "Healthy recipes and nutrition", value: "R250+/month" },
  ]

  return (
    <Layout>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="min-h-screen"
      >
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-background to-primary/5" />
          <img
            src={IMAGES.FAMILY_PHONE_5}
            alt="Family using Sammo"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div variants={fadeInUp}>
                <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                  One membership. Everyday support for the whole family.
                </h1>
              </motion.div>
              <motion.p
                variants={fadeInUp}
                className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
              >
                Up to 5 family members. 22+ assistants. Practical help across money, education, health, and daily life through familiar messaging platforms.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Button size="lg" className="text-lg px-8" asChild>
                  <Link to={ROUTE_PATHS.CONTACT}>Start Free Trial</Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="What You Get"
              headline="Everything your family needs"
              subheadline="Practical support delivered through WhatsApp - no app downloads, no complexity"
              align="center"
              className="mb-16"
            />
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
            >
              {benefits.map((benefit) => {
                const Icon = benefit.icon
                return (
                  <motion.div
                    key={benefit.title}
                    variants={staggerItem}
                    className="bg-card rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-accent" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="22+ Assistants"
              headline="Practical help across all areas of life"
              subheadline="From money and education to health and home - support for every family need"
              align="center"
              className="mb-16"
            />
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto"
            >
              {ASSISTANTS_LIST.map((assistant) => (
                <motion.div key={assistant.id} variants={staggerItem}>
                  <AssistantCard assistant={assistant} compact />
                </motion.div>
              ))}
            </motion.div>
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Button variant="outline" size="lg" asChild>
                <Link to={ROUTE_PATHS.ASSISTANTS}>View All Assistants</Link>
              </Button>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Real Value"
              headline="Save money while getting better support"
              subheadline="Sammo replaces multiple expensive services with one affordable membership"
              align="center"
              className="mb-16"
            />
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto"
            >
              {savings.map((saving) => {
                const Icon = saving.icon
                return (
                  <motion.div
                    key={saving.title}
                    variants={staggerItem}
                    className="bg-card rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 mx-auto">
                      <Icon className="w-6 h-6 text-accent" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{saving.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{saving.description}</p>
                    <p className="text-accent font-semibold">{saving.value}</p>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Pricing"
              headline="Choose the plan that fits your family"
              subheadline="All plans include WhatsApp delivery, local language support, and step-by-step guidance"
              align="center"
              className="mb-12"
            />

            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex items-center justify-center gap-4 mb-12"
            >
              <span className={billingCycle === "monthly" ? "font-semibold" : "text-muted-foreground"}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")}
                className="relative w-14 h-7 bg-accent rounded-full transition-colors"
                aria-label="Toggle billing cycle"
              >
                <motion.div
                  className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-sm"
                  animate={{ x: billingCycle === "annual" ? 28 : 0 }}
                  transition={springPresets.snappy}
                />
              </button>
              <span className={billingCycle === "annual" ? "font-semibold" : "text-muted-foreground"}>
                Annual
                <span className="ml-2 text-xs bg-accent/10 text-accent px-2 py-1 rounded-full">Save 20%</span>
              </span>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            >
              {PRICING_PLANS.map((plan) => {
                const price = billingCycle === "monthly" ? plan.monthlyPrice : Math.round(plan.annualPrice / 12)
                const totalAnnual = billingCycle === "annual" ? plan.annualPrice : plan.monthlyPrice * 12

                return (
                  <motion.div
                    key={plan.id}
                    variants={staggerItem}
                    className={`relative bg-card rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all ${
                      plan.highlighted
                        ? "ring-2 ring-accent scale-105 shadow-xl"
                        : ""
                    }`}
                  >
                    {plan.highlighted && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </div>
                    )}
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold">R{price}</span>
                        <span className="text-muted-foreground">/month</span>
                      </div>
                      {billingCycle === "annual" && (
                        <p className="text-sm text-muted-foreground mt-2">
                          R{totalAnnual} billed annually
                        </p>
                      )}
                    </div>

                    <div className="space-y-4 mb-8">
                      <div className="flex items-center gap-3 text-sm">
                        <Users className="w-5 h-5 text-accent flex-shrink-0" />
                        <span>
                          <strong>{plan.members}</strong> family {plan.members === 1 ? "member" : "members"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Sparkles className="w-5 h-5 text-accent flex-shrink-0" />
                        <span>
                          <strong>{plan.assistants}</strong> assistants
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <MessageSquare className="w-5 h-5 text-accent flex-shrink-0" />
                        <span>
                          <strong>{plan.messages}</strong> messages/month
                        </span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm">
                          <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className="w-full"
                      variant={plan.highlighted ? "default" : "outline"}
                      size="lg"
                      asChild
                    >
                      <Link to={ROUTE_PATHS.CONTACT}>
                        {plan.id === "free" ? "Start Free" : "Get Started"}
                      </Link>
                    </Button>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-br from-primary via-primary/95 to-accent/20 text-primary-foreground relative overflow-hidden">
          <img
            src={IMAGES.FAMILY_TECH_4}
            alt="Family support"
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/70 to-primary/80" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Start with one question today
              </h2>
              <p className="text-xl text-primary-foreground/90 mb-8">
                No credit card required. Try Sammo free and see how practical support can help your family.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to={ROUTE_PATHS.CONTACT}>Start Free Trial</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-transparent border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10" asChild>
                  <Link to={ROUTE_PATHS.ASSISTANTS}>Explore Assistants</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </Layout>
  )
}